import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';
import {
  booksService,
  BooksListParams,
  BooksListResponse,
  BookDetailResponse,
  CreateBookBody,
  UpdateBookBody,
  ToggleStatusResponse,
  UpdateSimulationEligibilityBody,
  BookVersionResponse,
  CreateBookVersionBody,
  UpdateBookVersionBody,
  BookCategoriesResponse,
  CreateBookCategoriesBody,
  UpdateBookCategoryBody,
} from '../../services/books.service';

export const bookKeys = {
  all: ['books'] as const,
  lists: () => [...bookKeys.all, 'list'] as const,
  list: (params: BooksListParams) => [...bookKeys.lists(), params] as const,
  details: () => [...bookKeys.all, 'detail'] as const,
  detail: (id: number) => [...bookKeys.details(), id] as const,
  versions: () => [...bookKeys.all, 'versions'] as const,
  version: (id: number) => [...bookKeys.versions(), id] as const,
  categories: () => [...bookKeys.all, 'categories'] as const,
  category: (id: number) => [...bookKeys.categories(), id] as const,
};

export function useBooks(
  params: BooksListParams = {},
  options?: Omit<
    UseQueryOptions<BooksListResponse, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<BooksListResponse, Error>({
    queryKey: bookKeys.list(params),
    queryFn: () => booksService.getList(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useGetBook(
  id: number,
  options?: Omit<UseQueryOptions<BookDetailResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<BookDetailResponse, Error>({
    queryKey: bookKeys.detail(id),
    queryFn: () => booksService.getById(id),
    enabled: id > 0,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useCreateBook(
  options?: UseMutationOptions<BookDetailResponse, Error, CreateBookBody>
) {
  const queryClient = useQueryClient();
  return useMutation<BookDetailResponse, Error, CreateBookBody>({
    mutationFn: (body) => booksService.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
    },
    ...options,
  });
}

export function useUpdateBook(
  options?: UseMutationOptions<BookDetailResponse, Error, { id: number; body: UpdateBookBody }>
) {
  const queryClient = useQueryClient();
  return useMutation<BookDetailResponse, Error, { id: number; body: UpdateBookBody }>({
    mutationFn: ({ id, body }) => booksService.update(id, body),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookKeys.detail(id) });
    },
    ...options,
  });
}

export function useToggleBookStatus() {
  const queryClient = useQueryClient();
  return useMutation<ToggleStatusResponse, Error, number>({
    mutationFn: (id: number) => booksService.toggleStatus(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookKeys.detail(id) });
    },
  });
}

export function useUpdateBookSimulationEligibility() {
  const queryClient = useQueryClient();
  return useMutation<
    ToggleStatusResponse,
    Error,
    { id: number; body: UpdateSimulationEligibilityBody }
  >({
    mutationFn: ({ id, body }) => booksService.updateSimulationEligibility(id, body),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookKeys.detail(id) });
    },
  });
}

export function useGetBookVersions(
  bookId: number,
  options?: Omit<UseQueryOptions<BookVersionResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<BookVersionResponse, Error>({
    queryKey: bookKeys.version(bookId),
    queryFn: () => booksService.getBookVersions(bookId),
    enabled: bookId > 0,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useCreateBookVersion(
  options?: UseMutationOptions<BookVersionResponse, Error, CreateBookVersionBody>
) {
  const queryClient = useQueryClient();
  return useMutation<BookVersionResponse, Error, CreateBookVersionBody>({
    mutationFn: (body) => booksService.createBookVersion(body),
    onSuccess: (_data, { book_id }) => {
      queryClient.invalidateQueries({ queryKey: bookKeys.version(book_id) });
      queryClient.invalidateQueries({ queryKey: bookKeys.detail(book_id) });
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
    },
    ...options,
  });
}

export function useUpdateBookVersion(
  options?: UseMutationOptions<
    BookVersionResponse,
    Error,
    { versionId: number; bookId: number; body: UpdateBookVersionBody }
  >
) {
  const queryClient = useQueryClient();
  return useMutation<
    BookVersionResponse,
    Error,
    { versionId: number; bookId: number; body: UpdateBookVersionBody }
  >({
    mutationFn: ({ versionId, body }) => booksService.updateBookVersion(versionId, body),
    onSuccess: (_data, { bookId }) => {
      queryClient.invalidateQueries({ queryKey: bookKeys.version(bookId) });
      queryClient.invalidateQueries({ queryKey: bookKeys.detail(bookId) });
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
    },
    ...options,
  });
}

export function useGetBookCategories(
  bookId: number,
  options?: Omit<UseQueryOptions<BookCategoriesResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<BookCategoriesResponse, Error>({
    queryKey: bookKeys.category(bookId),
    queryFn: () => booksService.getBookCategories(bookId),
    enabled: bookId > 0,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useCreateBookCategories(
  options?: UseMutationOptions<BookCategoriesResponse, Error, CreateBookCategoriesBody>
) {
  const queryClient = useQueryClient();
  return useMutation<BookCategoriesResponse, Error, CreateBookCategoriesBody>({
    mutationFn: (body) => booksService.createBookCategories(body),
    onSuccess: (_data, { book_id }) => {
      queryClient.invalidateQueries({ queryKey: bookKeys.category(book_id) });
      queryClient.invalidateQueries({ queryKey: bookKeys.detail(book_id) });
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
    },
    ...options,
  });
}

export function useUpdateBookCategory(
  options?: UseMutationOptions<
    BookCategoriesResponse,
    Error,
    { bookCategoryId: number; bookId: number; body: UpdateBookCategoryBody }
  >
) {
  const queryClient = useQueryClient();
  return useMutation<
    BookCategoriesResponse,
    Error,
    { bookCategoryId: number; bookId: number; body: UpdateBookCategoryBody }
  >({
    mutationFn: ({ bookCategoryId, body }) =>
      booksService.updateBookCategory(bookCategoryId, body),
    onSuccess: (_data, { bookId }) => {
      queryClient.invalidateQueries({ queryKey: bookKeys.category(bookId) });
      queryClient.invalidateQueries({ queryKey: bookKeys.detail(bookId) });
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
    },
    ...options,
  });
}

export function useDeleteBookCategory(
  options?: UseMutationOptions<void, Error, { bookCategoryId: number; bookId: number }>
) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { bookCategoryId: number; bookId: number }>({
    mutationFn: ({ bookCategoryId }) => booksService.deleteBookCategory(bookCategoryId),
    onSuccess: (_data, { bookId }) => {
      queryClient.invalidateQueries({ queryKey: bookKeys.category(bookId) });
      queryClient.invalidateQueries({ queryKey: bookKeys.detail(bookId) });
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
    },
    ...options,
  });
}
